/**
 * Normalize an error message into a stable grouping key by truncating at the
 * first quote and masking volatile tokens (uuids, numbers, timestamps). Shared
 * so the unknown-error digest (eluve-team-ops) and the api-nest logger emit the
 * SAME key — the digest's Slack clusters and the dashboard's `@errorGroup`
 * breakdown then line up by construction.
 */
export const generateGroupingKey = (message: string | undefined): string => {
  if (!message) {
    return '[NO_MESSAGE]';
  }

  // First clean the message to remove complex patterns
  const cleaned = cleanErrorMessage(message);

  return (
    cleaned
      // Replace timestamps
      .replace(
        /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d{3})?[Z]?/g,
        '[TIMESTAMP]',
      )
      // Replace epoch timestamps
      .replace(/\d{13}/g, '[EPOCH_MS]')
      .replace(/\d{10}/g, '[EPOCH]')
      // Replace UUIDs
      .replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '[UUID]',
      )
      // Replace timing patterns like "(44.39ms)" or "(35.31ms)"
      .replace(/\(\d+\.\d+ms\)/g, '([DURATION])')
      // Replace duration patterns like "44.39ms:" or "35.31ms):"
      .replace(/\d+\.\d+ms\)/g, '[DURATION])')
      .replace(/\d+\.\d+ms:/g, '[DURATION]:')
      // Replace decimal numbers (like durations, percentages, etc.)
      .replace(/\d+\.\d+/g, '[DECIMAL]')
      // Replace HTML fragments with consistent pattern
      .replace(/"<html><hea"[^"]*"/g, '"[HTML_FRAGMENT]"')
      .replace(/<html><hea[^>]*>/g, '[HTML_START]')
      // Replace other generic numbers (keep this last to avoid conflicts)
      .replace(/\b\d+\b/g, '[NUMBER]')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
};

export const cleanErrorMessage = (message: string): string => {
  // Truncate message at the first single or double quote to remove complex quoted content
  const quoteIndex = message.search(/['"]/);
  if (quoteIndex !== -1) {
    return message.substring(0, quoteIndex).trim();
  }
  return message.trim();
};
